import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { getTransactionById } from "../../services/api";
import { formatAmount, getMaxDecimalsForCurrency } from "../../utils";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const statusLabel = (s) => {
  if (s === "success") return "Received";
  if (s === "withdrawn") return "Withdrawn";
  if (s === "processing" || s === "pending" || s === "created") return "Processing";
  if (s === "failed" || s === "rejected") return "Failed";
  if (s === "refunded") return "Refunded";
  if (s === "canceled") return "Canceled";
  return s ?? "—";
};

const invoiceStatusLabel = (s) => {
  if (s === "success" || s === "fulfilled") return "Paid";
  if (s === "processing" || s === "quote_locked") return "In Progress";
  if (s === "created") return "Pending";
  return s ?? "—";
};

const statusBadgeClass = (s) => {
  if (s === "success")
    return "bg-green-50 text-green-700 border border-green-200";
  if (s === "withdrawn")
    return "bg-purple-50 text-purple-700 border border-purple-200";
  if (s === "failed" || s === "rejected")
    return "bg-red-50 text-red-700 border border-red-200";
  return "bg-orange-50 text-orange-700 border border-orange-200";
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between py-3 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500 w-40 flex-shrink-0">{label}</span>
    <span className="text-sm font-medium text-gray-900 text-right break-all">{value ?? "—"}</span>
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5">
    <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-100">
      {title}
    </h3>
    {children}
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────
const TransactionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [txn, setTxn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getTransactionById(id)
      .then((res) => {
        setTxn(res.data || null);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  // ─── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <AppShell>
        <div className="animate-pulse space-y-5">
          <div className="h-6 bg-gray-200 rounded w-48" />
          <div className="h-8 bg-gray-200 rounded w-72" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="h-64 bg-gray-200 rounded-xl" />
            <div className="h-64 bg-gray-200 rounded-xl" />
            <div className="h-48 bg-gray-200 rounded-xl" />
            <div className="h-48 bg-gray-200 rounded-xl" />
          </div>
        </div>
      </AppShell>
    );
  }

  // ─── Error / not found ─────────────────────────────────────────────────────
  if (error || !txn) {
    return (
      <AppShell>
        <button
          onClick={() => navigate("/receiver/transactions")}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Transactions
        </button>
        <p className="text-gray-500">Transaction not found.</p>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Transaction Detail</h1>
        </div>
        <span
          className={`text-sm font-semibold px-3 py-1.5 rounded-full ${statusBadgeClass(txn.status)}`}
        >
          {statusLabel(txn.status)}
        </span>
      </div>

      {/* Amount highlight — at top, matching sender layout */}
      {txn.status === "success" && (
        <div className="mb-5 bg-green-50 border border-green-200 rounded-xl px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-green-600 uppercase tracking-wider">
              Amount Received
            </p>
            <p className="text-3xl font-bold text-green-600 mt-0.5">
              {txn.quote ? `${txn.quote.toCurrency} ${formatAmount(txn.quote.toAmount,getMaxDecimalsForCurrency(txn.quote.toCurrency))}` : "—"}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ── Transaction summary ──────────────────────────────────────────── */}
        <SectionCard title="Transaction Summary">
          {/* Fix 1: API uses transactionId, not id */}
          <DetailRow label="Transaction ID" value={txn.transactionId || txn.id} />
          {txn.partnerTxnId && (
            <DetailRow label="Partner Txn ID" value={txn.partnerTxnId} />
          )}
          <DetailRow label="Status" value={statusLabel(txn.status)} />
          <DetailRow label="Created" value={formatDate(txn.createdAt)} />
        </SectionCard>

        {/* ── Invoice details ──────────────────────────────────────────────── */}
        {txn.paymentRequest ? (
          <SectionCard title="Invoice Details">
            <DetailRow label="Invoice ID" value={txn.paymentRequest.invoiceId} />
            <DetailRow
              label="Amount"
              value={
                txn.quote?.toAmount != null
                  ? `${txn.quote.toCurrency || ""} ${formatAmount(txn.quote.toAmount,getMaxDecimalsForCurrency(txn.quote.toCurrency))}`
                  : "—"
              }
            />

            <DetailRow label="Currency" value={txn.quote?.toCurrency} />
            {txn.paymentRequest.dueDate && (
              <DetailRow
                label="Due Date"
                value={new Date(txn.paymentRequest.dueDate).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              />
            )}
            <DetailRow label="Invoice Status" value={invoiceStatusLabel(txn.paymentRequest.status)} />
          </SectionCard>
        ) : (
          <SectionCard title="Invoice Details">
            <p className="text-sm text-gray-400">No invoice details available.</p>
          </SectionCard>
        )}

        {/* ── Sender details ───────────────────────────────────────────────── */}
        <SectionCard title="Sender">
          {txn.fromDetails?.name ? (
            <>
              <DetailRow label="Name" value={txn.fromDetails.name} />
              {txn.fromDetails.email && <DetailRow label="Email" value={txn.fromDetails.email} />}
              {txn.fromDetails.phone && <DetailRow label="Phone" value={txn.fromDetails.phone} />}
              {txn.fromDetails.country && <DetailRow label="Country" value={txn.fromDetails.country} />}
            </>
          ) : (
            <p className="text-sm text-gray-400">No sender information available.</p>
          )}
        </SectionCard>
      </div>
    </AppShell>
  );
};

export default TransactionDetail;
